import React, { useEffect, useRef, useState } from "react";
import { useSwipe, useIsMobile } from "../lib/useSwipe";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Mobile-first horizontal carousel with snap-back transitions.
 *
 * On mobile (≤768px viewport by default): renders children as a single-track
 * horizontal slider with touch swipe, dot indicators, and left/right nav.
 *
 * On desktop: by default falls back to vertical stack (so the layout still
 * works on a 1920px monitor). Pass `mobileOnly={false}` to keep the swiper
 * behaviour everywhere.
 *
 * Children: an array — each item rendered as one full-width slide.
 *
 * Props:
 *   index       — controlled current slide (optional)
 *   onIndexChange(n)
 *   testid      — root data-testid for tests
 *   showDots    — bool (default true)
 *   showArrows  — bool (default true)
 *   mobileOnly  — bool (default true)
 */
export default function MobileCarousel({
  children,
  index: indexProp,
  onIndexChange,
  testid = "mobile-carousel",
  showDots = true,
  showArrows = true,
  mobileOnly = true,
}) {
  const items = React.Children.toArray(children).filter(Boolean);
  const isMobile = useIsMobile();
  const isControlled = typeof indexProp === "number";
  const [internalIndex, setInternalIndex] = useState(0);
  const index = isControlled ? indexProp : internalIndex;

  // Keep index in bounds whenever the items count changes.
  useEffect(() => {
    if (index > items.length - 1) {
      const next = Math.max(0, items.length - 1);
      if (isControlled) onIndexChange?.(next);
      else setInternalIndex(next);
    }
  }, [items.length, index, isControlled, onIndexChange]);

  const setIndex = (next) => {
    const clamped = Math.max(0, Math.min(items.length - 1, next));
    if (isControlled) onIndexChange?.(clamped);
    else setInternalIndex(clamped);
  };

  const { bind } = useSwipe({
    enabled: isMobile || !mobileOnly,
    onSwipeLeft: () => setIndex(index + 1),
    onSwipeRight: () => setIndex(index - 1),
  });

  // Desktop fallback — stacked layout
  if (mobileOnly && !isMobile) {
    return (
      <div className="space-y-3" data-testid={testid}>
        {items.map((c, i) => (
          <div key={i} data-testid={`${testid}-slide-${i}`}>{c}</div>
        ))}
      </div>
    );
  }

  return (
    <div data-testid={testid}>
      <div className="relative overflow-hidden" {...bind}>
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {items.map((c, i) => (
            <div
              key={i}
              className="w-full shrink-0 px-0.5"
              aria-hidden={i !== index}
              data-testid={`${testid}-slide-${i}`}
            >
              {c}
            </div>
          ))}
        </div>
      </div>

      {(showDots || showArrows) && items.length > 1 && (
        <div className="flex items-center justify-between gap-3 mt-3">
          {showArrows && (
            <button
              type="button"
              onClick={() => setIndex(index - 1)}
              disabled={index === 0}
              className="text-white/60 hover:text-white p-2 disabled:opacity-20"
              aria-label="Previous"
              data-testid={`${testid}-prev`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}

          {showDots && (
            <div className="flex-1 flex items-center justify-center gap-1.5" data-testid={`${testid}-dots`}>
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-1.5 transition-all ${
                    i === index
                      ? "w-6 bg-[#6B9BFF]"
                      : "w-1.5 bg-white/30 hover:bg-white/60"
                  }`}
                  data-testid={`${testid}-dot-${i}`}
                />
              ))}
            </div>
          )}

          {showArrows && (
            <button
              type="button"
              onClick={() => setIndex(index + 1)}
              disabled={index === items.length - 1}
              className="text-white/60 hover:text-white p-2 disabled:opacity-20"
              aria-label="Next"
              data-testid={`${testid}-next`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
